// src/components/CycleSettings/ChannelsTable.tsx

import React from 'react';
import TableTh from '../../TableTh/TableTh';
import { DateFormatter } from '../../../utils/date-formatter';
import { DateFormats } from '../../../enums/date-formats';
import { settingOptions, settingByMode } from '../config/index';
import CycleSettingsEnum from '../../../enums/cycle-setting.enum';
import { IDbCycleSettings } from '../../../store/models/cycles';
import { DiffResult } from '../helpers';
import ChannelSettingsDiff from './ChannelSettingsDiff';
import CycleMeasurement from '../../../enums/cycle-measurement';
import CycleMeasurementSymbol from '../../../enums/cycle-measurement-symbol';

interface Props {
  channelsSettings: any; // Use a more specific type from your store
  cycleDbSettings: IDbCycleSettings;
  measurement: CycleMeasurement;
}

const rateFields = new Set(['upperRampTolerance', 'lowerRampTolerance', 'rate']);
const temperatureFields = new Set([
  'upperHoldTolerance',
  'lowerHoldTolerance',
  'holdingTemperature',
  'soakUpper',
  'soakLower',
]);

const getUnitLabel = (fieldName: string, measurement: CycleMeasurement) => {
  const symbol = CycleMeasurementSymbol[measurement];

  if (rateFields.has(fieldName)) {
    return `${symbol}/h`;
  }

  if (temperatureFields.has(fieldName)) {
    return symbol;
  }

  return null;
};

const ChannelsTable: React.FC<Props> = ({ channelsSettings, cycleDbSettings, measurement }) => {
  const { channels, lastUpdatedAt } = channelsSettings;
  const { mode } = cycleDbSettings;

  const [dataDiffs, setDataDiffs] = React.useState(null);

  const getDataDiffs = (channelId: string, currectDiffs: DiffResult[]) => {
    setDataDiffs({ channelId, diffs: currectDiffs });
  };

  const clearDataDiffs = () => {
    setDataDiffs(null);
  };

  const preparedSettings = React.useMemo(() => {
    if (!settingByMode[mode]) return [];

    if (mode === CycleSettingsEnum.PostWeld) {
      return settingByMode[mode](channels, mode, cycleDbSettings, getDataDiffs, measurement);
    }
    return settingByMode[mode](channels, mode, measurement);
  }, [channels, mode, cycleDbSettings, measurement]);

  const tableHeaders = React.useMemo(
    () =>
      (settingOptions[mode] || []).map((option) => {
        const unit = getUnitLabel(option.fieldName, measurement);

        return unit ? { ...option, labelText: `${option.labelText} (${unit})` } : option;
      }),
    [mode, measurement],
  );

  return (
    <>
      <div className="w-full sm:p-10">
        <div id="channels-table-container" className="c-screen-cycles-table overflow-x-auto font-medium">
          <p className="text-nowrap">
            Channels Settings -{' '}
            <span>
              last updated at {DateFormatter({ date: lastUpdatedAt, format: DateFormats['dd-MMM yyyy hh:mm:ss a'] })}
            </span>
          </p>
          <table className="c-screen-cycles-table__table w-full">
            <thead className="c-screen-cycles-table__thead text-nowrap text-xs">
              <tr>
                {tableHeaders.map((optionKey) => (
                  <TableTh key={optionKey.labelText} option={{ NAME: optionKey.labelText, KEY: optionKey.labelText }} />
                ))}
              </tr>
            </thead>
            <tbody className="c-screen-cycles-table__tbody whitespace-nowrap">
              {preparedSettings.map((channelSetting) => (
                <tr
                  className={
                    'c-screen-cycle-table-row text-sm shadow-inset-table-top odd:bg-white even:bg-gray-2 hover:bg-stroke'
                  }
                  key={channelSetting.name}
                >
                  {tableHeaders.map((optionKey) => (
                    <td className="c-cycle-table__td py-1 text-left" key={optionKey.fieldName}>
                      <span
                        className={`rounded p-1 ${
                          !channelSetting.isSameMode && optionKey.fieldName === 'mode' ? 'bg-red text-white' : ''
                        }`}
                      >
                        {channelSetting[optionKey.fieldName]}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ChannelSettingsDiff
        dataDifferences={dataDiffs}
        isOpenModal={dataDiffs && dataDiffs.diffs.length > 0}
        closeModal={clearDataDiffs}
      />
    </>
  );
};

export default ChannelsTable;
